/**
 * BrowserPrintAgent
 * ==================
 * Componente "headless" montato nel Layout principale.
 * Ascolta gli eventi WebSocket `print_job_ready` e, se in questo browser è pairata
 * una stampante USB/Bluetooth con vendor/product/device matching, stampa direttamente
 * il job. Nessun bridge necessario.
 *
 * ⚠️ SERIALIZZAZIONE: le stampe USB sono SERIALIZZATE in una coda FIFO. Se arrivano
 * 3 print_job_ready in 100ms (es. 3 invii in cucina consecutivi), NON si invocano
 * 3 `claimInterface` paralleli (che fallirebbero perché USB è single-claim). I job
 * vengono processati uno alla volta nell'ordine di arrivo.
 *
 * REQUISITI: il browser deve essere Chrome o Edge, e il tab di questo browser deve
 * restare aperto sul PC fisicamente connesso alla stampante.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useWS } from "@/contexts/WebSocketContext";
import { webusb, webbt } from "@/lib/browserPrinting";
import { markJobProcessed, isJobProcessed, tryAcquirePrintLock } from "@/lib/processedJobs";

const BrowserPrintAgent = () => {
    const { subscribe } = useWS() || {};
    // Coda FIFO + flag "in corso"
    const queueRef = useRef([]);
    const processingRef = useRef(false);

    useEffect(() => {
        if (!subscribe) return;

        const ackBackend = async (jobId, success, errorMsg) => {
            try {
                await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/print/ack/${jobId}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("rm_token") || ""}`,
                    },
                    body: JSON.stringify({ success, error: errorMsg || "" }),
                    keepalive: true,
                });
            } catch (_e) { /* best effort */ }
        };

        const reportFailure = async (job, errMsg) => {
            try {
                await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/print/report-failure`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("rm_token") || ""}`,
                    },
                    body: JSON.stringify({
                        job_id: job.id,
                        printer_name: job.printer_name,
                        destination: job.destination,
                        order_id: job.meta?.order_id,
                        table_label: job.meta?.table_label,
                        sent_by_user_id: job.meta?.sent_by_user_id,
                        error: errMsg,
                    }),
                    keepalive: true,
                });
            } catch (_e) { /* best effort */ }
        };

        const processOne = async ({ job, match }) => {
            try {
                if (match.protocol === "usb" && match.usb_vendor_id && match.usb_product_id) {
                    if (!webusb.supported()) {
                        console.warn("[BrowserPrintAgent] WebUSB non supportato in questo browser");
                        return;
                    }
                    const dev = await webusb.findPairedDevice(match.usb_vendor_id, match.usb_product_id);
                    if (!dev) {
                        console.warn(`[BrowserPrintAgent] Stampante VID=${match.usb_vendor_id.toString(16)} PID=${match.usb_product_id.toString(16)} NON pairata in questo browser.`);
                        return; // questo browser non ha il pairing — altro browser stamperà
                    }
                    console.log("[BrowserPrintAgent] 🖨️  Stampa job", job.id?.slice(0, 8), "su", job.printer_name);
                    await webusb.printJob(job, match.usb_vendor_id, match.usb_product_id);
                    toast.success(`Stampato: ${job.printer_name || ""}`.trim());
                    console.log("[BrowserPrintAgent] ✅ Stampa OK", job.id?.slice(0, 8));
                    await ackBackend(job.id, true);
                } else if (match.protocol === "bluetooth" && match.bt_device_id) {
                    if (!webbt.supported()) return;
                    const dev = await webbt.findPairedDevice(match.bt_device_id);
                    if (!dev) {
                        console.warn(`[BrowserPrintAgent] BT device ${match.bt_device_id} non pairato in questo browser.`);
                        return;
                    }
                    await webbt.printJob(job, match.bt_device_id);
                    toast.success(`Stampato: ${job.printer_name || ""}`.trim());
                    await ackBackend(job.id, true);
                }
            } catch (err) {
                console.error("[BrowserPrintAgent] Stampa fallita:", err);
                toast.error(`Stampa fallita su ${job.printer_name}: ${err.message}`, { duration: 10000 });
                await ackBackend(job.id, false, err.message || String(err));
                await reportFailure(job, err.message || String(err));
            }
        };

        const drainQueue = async () => {
            if (processingRef.current) return; // un altro drain è già in corso
            processingRef.current = true;
            try {
                // Processa la coda in ordine FIFO finché vuota
                // (nuovi job che arrivano durante il drain vengono semplicemente accodati e processati)
                while (queueRef.current.length > 0) {
                    const item = queueRef.current.shift();
                    if (!item) continue;
                    await processOne(item);
                    // Micro-delay tra stampe successive: dà tempo al device USB di
                    // rilasciare completamente l'interfaccia prima del prossimo claim.
                    // 150ms è abbastanza per ESC/POS termiche; non rallenta apprezzabilmente
                    // la stampa percepita dal cameriere.
                    await new Promise(r => setTimeout(r, 150));
                }
            } finally {
                processingRef.current = false;
            }
        };

        const unsubscribe = subscribe((msg) => {
            if (msg?.type !== "print_job_ready") return;
            const job = msg.job || {};
            const match = msg.match || {};
            if (!job.id) return;
            // ⚠️ De-dup CONDIVISA (memoria + BroadcastChannel + localStorage tra tab)
            if (isJobProcessed(job.id)) {
                console.log("[BrowserPrintAgent] Skip job già processato:", job.id?.slice(0, 8));
                return;
            }
            // ⚠️ Lock cross-tab: solo UN tab dello stesso PC tenta la stampa USB
            if (!tryAcquirePrintLock(job.id)) {
                console.log("[BrowserPrintAgent] Skip: altro tab sta stampando job", job.id?.slice(0, 8));
                markJobProcessed(job.id);
                // Toast discreto e breve: informa l'operatore che il lock è normale
                toast.info(`🔒 Stampa gestita da altro tab del browser`, {
                    duration: 2000,
                    id: `lock-${job.id}`, // dedup toast su stesso job
                });
                return;
            }
            markJobProcessed(job.id);

            queueRef.current.push({ job, match });
            console.log("[BrowserPrintAgent] 📥 Job in coda:", job.id?.slice(0, 8), `(queue size: ${queueRef.current.length})`, msg.retry ? "(retry)" : "");
            drainQueue().catch(e => console.error("[BrowserPrintAgent] drainQueue error:", e));
        });

        // ⚠️ RECOVERY ALL'AVVIO + ogni 30s: chiama l'endpoint /print-jobs/recoverable
        // per recuperare job pending creati negli ultimi 60s (es. PC era in sleep o
        // ha avuto un glitch di rete, ha perso WS broadcast → li ricovera ora).
        const recoverPendingJobs = async () => {
            try {
                const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/print-jobs/recoverable`, {
                    headers: { "Authorization": `Bearer ${localStorage.getItem("rm_token") || ""}` },
                });
                if (!resp.ok) return;
                const items = await resp.json();
                if (!Array.isArray(items) || items.length === 0) return;
                let recovered = 0;
                for (const it of items) {
                    const j = it?.job;
                    if (!j?.id || isJobProcessed(j.id)) continue;
                    if (!tryAcquirePrintLock(j.id)) {
                        markJobProcessed(j.id);
                        continue;
                    }
                    markJobProcessed(j.id);
                    queueRef.current.push({ job: j, match: it.match || {} });
                    recovered++;
                }
                if (recovered > 0) {
                    console.log(`[BrowserPrintAgent] 🔄 Recovered ${recovered} pending jobs`);
                    drainQueue().catch(e => console.error("recover drain:", e));
                }
            } catch (e) {
                // best effort
            }
        };

        // Avvio: piccolo delay per dare tempo al token di stabilizzarsi
        const initTimer = setTimeout(recoverPendingJobs, 1500);
        // Periodico: ogni 30s
        const intervalTimer = setInterval(recoverPendingJobs, 30000);
        // Recovery anche quando la finestra torna visibile (dopo sleep/lock schermo)
        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                console.log("[BrowserPrintAgent] visibilitychange → recover pending");
                recoverPendingJobs();
            }
        };
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            unsubscribe();
            clearTimeout(initTimer);
            clearInterval(intervalTimer);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [subscribe]);

    return null;
};

export default BrowserPrintAgent;
